/**
 * Meta Pixel Cookie Manager
 * 
 * Manually creates _fbp and _fbc cookies when Meta Pixel is blocked.
 * This ensures we can still track Facebook/Instagram ad clicks even though
 * we're blocking Shopify's native Meta Pixel implementation.
 * 
 * MUST load BEFORE meta-pixel-block-international.js
 */

(function() {
  'use strict';
  
  // Cookie helper functions
  function setCookie(name, value, days, options) {
    const defaultOptions = {
      path: '/',
      sameSite: 'None',
      secure: true
    };
    
    const opts = { ...defaultOptions, ...options };
    
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    
    let cookieString = name + '=' + encodeURIComponent(value) + expires + '; path=' + opts.path;
    
    // Add domain if specified (use current domain or parent domain)
    if (opts.domain) {
      cookieString += '; domain=' + opts.domain;
    }
    
    // Add SameSite attribute
    if (opts.sameSite) {
      cookieString += '; SameSite=' + opts.sameSite;
    }
    
    // Add Secure flag (required for SameSite=None)
    if (opts.secure) {
      cookieString += '; Secure';
    }
    
    document.cookie = cookieString;
    
    if (window.location.search.includes('debug_cookies=true')) {
      console.log('[Meta Pixel Cookie Manager] Set cookie:', name, '=', value, 'Options:', opts);
    }
  }
  
  function getCookie(name) {
    try {
      const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
      if (match && match[2]) {
        return decodeURIComponent(match[2]);
      }
    } catch (e) {
      // Silently handle cookie reading errors
    }
    return null;
  }
  
  // Get current domain for cookie setting
  function getCookieDomain() {
    const hostname = window.location.hostname;
    
    // For production domains, use parent domain (e.g., .duavaa.com)
    // This allows cookies to work across subdomains
    if (hostname.includes('duavaa.com')) {
      // Extract root domain
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        return '.' + parts.slice(-2).join('.');
      }
    }
    
    // For localhost or other domains, don't set domain attribute
    // (browsers will use current domain automatically)
    return null;
  }
  
  // Generate random string for fbp cookie
  function generateRandomString(length) {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
  
  // Create _fbp cookie (Facebook Browser ID)
  function createFbpCookie() {
    const existingFbp = getCookie('_fbp');
    
    if (existingFbp) {
      if (window.location.search.includes('debug_cookies=true')) {
        console.log('[Meta Pixel Cookie Manager] _fbp cookie already exists:', existingFbp);
      }
      return existingFbp;
    }
    
    // Create new _fbp cookie
    // Format: fb.1.{timestamp}.{random}
    const timestamp = Math.floor(Date.now() / 1000);
    const random = generateRandomString(10);
    const fbpValue = `fb.1.${timestamp}.${random}`;
    
    const domain = getCookieDomain();
    setCookie('_fbp', fbpValue, 90, { domain: domain });
    
    if (window.location.search.includes('debug_cookies=true')) {
      console.log('[Meta Pixel Cookie Manager] ✅ Created _fbp cookie:', fbpValue);
    }
    
    return fbpValue;
  }
  
  // Create _fbc cookie (Facebook Click ID) from fbclid URL parameter
  function createFbcCookie() {
    // First, check if _fbc cookie already exists
    const existingFbc = getCookie('_fbc');
    
    // Validate existing cookie format
    if (existingFbc) {
      // Check if it's a valid format (not the .fbclid variant)
      if (!existingFbc.endsWith('.fbclid') && existingFbc.split('.').length >= 4) {
        if (window.location.search.includes('debug_cookies=true')) {
          console.log('[Meta Pixel Cookie Manager] _fbc cookie already exists:', existingFbc);
        }
        return existingFbc;
      }
    }
    
    // Try to get fbclid from URL parameters
    let fbclid = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      fbclid = urlParams.get('fbclid');
    } catch (e) {
      // Silently handle URL parsing errors
    }
    
    // If not in URL, check sessionStorage (might have been stored on previous page)
    if (!fbclid) {
      try {
        fbclid = sessionStorage.getItem('fbclid');
      } catch (e) {
        // Silently handle sessionStorage errors
      }
    }
    
    // If we have fbclid, create _fbc cookie
    if (fbclid) {
      // Format: fb.1.{timestamp}.{fbclid}
      const timestamp = Math.floor(Date.now() / 1000);
      const fbcValue = `fb.1.${timestamp}.${fbclid}`;
      
      const domain = getCookieDomain();
      setCookie('_fbc', fbcValue, 90, { domain: domain });
      
      // Store fbclid in sessionStorage for persistence across navigation
      try {
        sessionStorage.setItem('fbclid', fbclid);
      } catch (e) {
        // Silently handle sessionStorage errors
      }
      
      if (window.location.search.includes('debug_cookies=true')) {
        console.log('[Meta Pixel Cookie Manager] ✅ Created _fbc cookie from fbclid:', fbcValue);
      }
      
      return fbcValue;
    }
    
    if (window.location.search.includes('debug_cookies=true')) {
      console.log('[Meta Pixel Cookie Manager] No fbclid found in URL or sessionStorage, skipping _fbc creation');
    }
    
    return null;
  }
  
  // Initialize cookies
  function initializeCookies() {
    // Create _fbp cookie if it doesn't exist
    createFbpCookie();
    
    // Create _fbc cookie if fbclid is present
    createFbcCookie();
  }
  
  // Run immediately (don't wait for DOM)
  // This ensures cookies are set before any other scripts try to read them
  initializeCookies();
  
  // Also run on DOMContentLoaded as a fallback (in case script loaded late)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCookies);
  } else {
    // DOM already loaded, run again to catch any missed cases
    setTimeout(initializeCookies, 100);
  }
  
  // Expose functions for debugging
  if (window.location.search.includes('debug_cookies=true')) {
    window.MetaPixelCookieManager = {
      createFbpCookie: createFbpCookie,
      createFbcCookie: createFbcCookie,
      getCookie: getCookie,
      initializeCookies: initializeCookies
    };
    console.log('[Meta Pixel Cookie Manager] ✅ Initialized. Debug functions available at window.MetaPixelCookieManager');
  }
  
})();
